/**
 * Format a USD amount with optional INR conversion suffix.
 *
 *   formatUSD(12)              -> "$12.00"
 *   formatUSD(12, 83.5)        -> "$12.00 (₹996.00)"
 *   formatUSD(12, 83.5, true)  -> "$12.00 (₹996.00 • incl. GST: ₹1175.28)"
 *
 * The GST factor is applied multiplicatively (18% by default) when withGST is
 * true. Use this everywhere pricing is shown so the UI is consistent.
 */
export function formatUSD(usd: number, inrRate?: number, withGST = false, gstPct = 18): string {
  const base = `$${usd.toFixed(2)}`;
  if (!inrRate) return base;
  const inr = usd * inrRate;
  if (!withGST) return `${base} (₹${inr.toFixed(2)})`;
  const gst = inr * (1 + gstPct / 100);
  return `${base} (₹${inr.toFixed(2)} • incl. GST: ₹${gst.toFixed(2)})`;
}

/**
 * Format an INR amount. If withGST is true, the value is treated as pre-GST.
 *
 *   formatINR(996)                 -> "₹996.00"
 *   formatINR(996, true)           -> "₹996.00 (incl. GST: ₹1175.28)"
 */
export function formatINR(inr: number, withGST = false, gstPct = 18): string {
  const base = `₹${inr.toFixed(2)}`;
  if (!withGST) return base;
  const gst = inr * (1 + gstPct / 100);
  return `${base} (incl. GST: ₹${gst.toFixed(2)})`;
}

/**
 * Convert bytes to TB as a float (precision = 3 decimals) - useful for the
 * pricing calculator's "amount of storage" input.
 */
export function bytesToTB(bytes: number): number {
  return bytes / (1024 ** 4);
}

/** Inverse: TB string from the calculator → bytes for the API. */
export function tbToBytes(tb: number): number {
  return Math.floor(tb * (1024 ** 4));
}
